import { useCallback, useEffect } from 'react';
import { useCarouselContext } from 'root/src/client/v2/common/components/Carousel/Carousel';
// Cast rather than a plain read: the plugin's module augmentation declares `autoplay` as always
// present on EmblaPluginsType, but a carousel that mounts without the plugin (single slide) has
// nothing there at runtime.
const getAutoplay = (emblaApi) => emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.plugins().autoplay;
/**
 * @description
 * Manual control over an embla carousel's autoplay countdown. Must be called inside a Carousel `Root`.
 *
 * The plugin re-arms its timer on drag, hover and its own tick, but never on a programmatic scroll,
 * so arrows and dots leave the countdown running from wherever it already was: press "next" a moment
 * before it fires and the slide you asked for is overtaken by the one after it. Route manual controls
 * through `resetAutoplay` (restart the countdown) or `stopAutoplay` (hand control to the visitor for
 * good) so a chosen slide is never yanked away.
 */
export const useCarouselAutoplay = () => {
    const { emblaApi } = useCarouselContext();
    const stopAutoplay = useCallback(() => { var _a; return (_a = getAutoplay(emblaApi)) === null || _a === void 0 ? void 0 : _a.stop(); }, [emblaApi]);
    const resetAutoplay = useCallback(() => { var _a; return (_a = getAutoplay(emblaApi)) === null || _a === void 0 ? void 0 : _a.reset(); }, [emblaApi]);
    return { emblaApi, stopAutoplay, resetAutoplay };
};
/**
 * @description
 * Stops autoplay once the visitor clicks into an embedded player.
 *
 * A cross-origin iframe swallows every event the carousel could listen for, and embla's own
 * `stopOnFocusIn` ignores focus that was not preceded by a Tab press, so nothing tells the carousel
 * a trailer started playing. The window losing focus to the iframe element does.
 */
export const useStopAutoplayOnEmbedFocus = (stopAutoplay) => {
    useEffect(() => {
        const onWindowBlur = () => {
            var _a;
            if (((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.tagName) === 'IFRAME') {
                stopAutoplay();
            }
        };
        window.addEventListener('blur', onWindowBlur);
        return () => window.removeEventListener('blur', onWindowBlur);
    }, [stopAutoplay]);
};
